import { AuthError, LoginFlow } from './types';
import Web3 from 'web3';
export class AuthProvider {
    constructor(config) {
        this.config = config;
        this.web3 = null;
        this.walletConnectProvider = null;
        this.ENDPOINTS = {
            TOKEN: '/connect/token',
            SIWE_NONCE: '/Account/Siwe/Nonce',
            SIWE_MESSAGE: '/Account/Siwe/Message',
            AUTHORIZE: '/connect/authorize'
        };
    }
    async initializeWeb3() {
        if (typeof window.ethereum === 'undefined') {
            return false;
        }
        try {
            // Create a custom provider that implements the required interface
            const provider = {
                sendAsync: (request, callback) => {
                    // Handle eth_accounts specially since it's used for connection
                    if (request.method === 'eth_accounts' || request.method === 'eth_requestAccounts') {
                        window.ethereum.request({ method: request.method })
                            .then(accounts => {
                            callback(null, {
                                jsonrpc: '2.0',
                                id: request.id,
                                result: accounts
                            });
                        })
                            .catch(error => {
                            callback(error, null);
                        });
                        return;
                    }
                    // For other methods like personal_sign
                    window.ethereum.request({
                        method: request.method,
                        params: request.params || []
                    })
                        .then(result => {
                        callback(null, {
                            jsonrpc: '2.0',
                            id: request.id,
                            result: result
                        });
                    })
                        .catch(error => {
                        callback(error, null);
                    });
                },
                send: (request, callback) => {
                    // Delegate to sendAsync for consistency
                    provider.sendAsync(request, callback);
                }
            };
            this.web3 = new Web3(provider);
            return true;
        }
        catch (error) {
            console.error('Failed to initialize Web3:', error);
            return false;
        }
    }
    async login(flow) {
        switch (flow) {
            case LoginFlow.SIWE:
                return this.handleSiweLogin();
            case LoginFlow.SIWE_WALLETCONNECT:
                return this.handleWalletConnectSiweLogin();
            case LoginFlow.MICROSOFT:
            case LoginFlow.GOOGLE:
            case LoginFlow.FACEBOOK:
                return this.handleSocialLogin(flow);
            default:
                throw new AuthError(`Unsupported login flow: ${flow}`, 'UNSUPPORTED_FLOW');
        }
    }
    async handleSiweLogin() {
        if (!this.web3) {
            const web3Available = await this.initializeWeb3();
            if (!web3Available) {
                throw new AuthError('MetaMask not installed', 'NO_ETHEREUM_PROVIDER');
            }
        }
        try {
            // Request wallet connection and get address
            const accounts = (await window.ethereum.request({ method: 'eth_requestAccounts' }));
            const walletAddress = accounts[0];
            const chainId = (await window.ethereum.request({ method: 'eth_chainId' }));
            // Get nonce
            const nonceData = await this.fetchSiweNonce(walletAddress);
            const messageData = await this.fetchSiweMessage(nonceData.nonce);
            // Sign message with Web3.js
            const signature = await this.signMessageWithWeb3(messageData.message, walletAddress);
            // Exchange signature for tokens
            return this.exchangeSiweToken(signature, walletAddress, chainId);
        }
        catch (error) {
            if (error instanceof AuthError) {
                throw error;
            }
            throw new AuthError('SIWE login failed', 'SIWE_FAILED');
        }
    }
    async handleWalletConnectSiweLogin() {
        const provider = await this.initializeWalletConnectProvider();
        await this.ensureWalletConnectConnected(provider);
        try {
            const accounts = (await provider.request({ method: 'eth_requestAccounts' }));
            if (!(accounts === null || accounts === void 0 ? void 0 : accounts.length)) {
                throw new AuthError('No wallet account returned by WalletConnect', 'WALLETCONNECT_NO_ACCOUNT');
            }
            const walletAddress = accounts[0];
            const chainIdResult = await provider.request({ method: 'eth_chainId' });
            const chainId = typeof chainIdResult === 'number' ? `0x${chainIdResult.toString(16)}` : String(chainIdResult);
            const nonceData = await this.fetchSiweNonce(walletAddress);
            const messageData = await this.fetchSiweMessage(nonceData.nonce);
            const signature = await this.signMessageWithWalletConnect(provider, messageData.message, walletAddress);
            return this.exchangeSiweToken(signature, walletAddress, chainId);
        }
        catch (error) {
            if (error instanceof AuthError) {
                throw error;
            }
            throw new AuthError(`WalletConnect SIWE login failed: ${this.getErrorMessage(error)}`, 'SIWE_WALLETCONNECT_FAILED');
        }
    }
    getSocialLoginHandler(flow) {
        switch (flow) {
            case LoginFlow.MICROSOFT:
                return 'MicrosoftLogin';
            case LoginFlow.GOOGLE:
                return 'GoogleLogin';
            case LoginFlow.FACEBOOK:
                return 'FacebookLogin';
            default:
                throw new AuthError(`Invalid social login flow: ${flow}`, 'INVALID_SOCIAL_FLOW');
        }
    }
    async handleSocialLogin(flow) {
        if (!this.config.redirectUri) {
            throw new AuthError('redirectUri is required for social login', 'MISSING_REDIRECT_URI');
        }
        // Generate a random state for security
        const state = Math.random().toString(36).substring(2, 15);
        // Store state in sessionStorage for verification after redirect
        sessionStorage.setItem('oauth_state', state);
        // Construct the OAuth parameters
        const oauthParams = new URLSearchParams({
            response_type: 'code',
            client_id: this.config.clientId,
            state: state,
            scope: this.config.scope,
            redirect_uri: this.config.redirectUri
        });
        // Construct the returnUrl (OAuth callback URL)
        const returnUrl = `/connect/authorize/callback?${oauthParams.toString()}`;
        // Get the appropriate handler for the social login provider
        const handler = this.getSocialLoginHandler(flow);
        // Construct the final login URL
        const loginUrl = new URL(`${this.config.baseUrl}/Account/Login`);
        loginUrl.searchParams.append('returnUrl', returnUrl);
        loginUrl.searchParams.append('handler', handler);
        // Redirect to social login page
        window.location.href = loginUrl.toString();
        // This code won't execute due to the redirect, but we need to return something
        // to satisfy the TypeScript return type
        throw new AuthError(`Redirecting to ${flow} login...`, 'REDIRECTING');
    }
    // Add a method to handle the OAuth callback
    async handleOAuthCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const storedState = sessionStorage.getItem('oauth_state');
        // Clear the stored state
        sessionStorage.removeItem('oauth_state');
        // If there's no code or state, we're not in a callback
        if (!code || !state) {
            return null;
        }
        // Verify state to prevent CSRF
        if (state !== storedState) {
            throw new AuthError('Invalid state parameter', 'INVALID_STATE');
        }
        // Exchange the authorization code for tokens
        const tokenResponse = await fetch(`${this.config.baseUrl}${this.ENDPOINTS.TOKEN}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: this.config.clientId,
                code: code,
                redirect_uri: this.config.redirectUri,
                scope: this.config.scope,
            })
        });
        if (!tokenResponse.ok) {
            throw new AuthError('Failed to get tokens', 'TOKEN_FAILED', tokenResponse.status);
        }
        return await tokenResponse.json();
    }
    async signMessageWithWeb3(message, walletAddress) {
        try {
            if (!this.web3) {
                throw new AuthError('Web3 not initialized', 'WEB3_NOT_INITIALIZED');
            }
            // Use empty string as password parameter for MetaMask
            return await this.web3.eth.personal.sign(this.web3.utils.utf8ToHex(message), walletAddress, '' // Empty string instead of null for MetaMask
            );
        }
        catch (error) {
            throw new AuthError('Failed to sign message', 'SIGN_FAILED');
        }
    }
    async initializeWalletConnectProvider() {
        var _a;
        if (this.walletConnectProvider) {
            return this.walletConnectProvider;
        }
        const projectId = this.config.walletConnectProjectId;
        if (!projectId) {
            throw new AuthError('WalletConnect project id is missing', 'WALLETCONNECT_PROJECT_ID_MISSING');
        }
        const chains = ((_a = this.config.walletConnectChains) === null || _a === void 0 ? void 0 : _a.length) ? this.config.walletConnectChains : [1];
        const optionalChains = chains;
        try {
            const { default: EthereumProvider } = (await import('@walletconnect/ethereum-provider'));
            this.walletConnectProvider = (await EthereumProvider.init({
                projectId,
                chains,
                optionalChains,
                showQrModal: true,
            }));
            return this.walletConnectProvider;
        }
        catch (error) {
            throw new AuthError(`Failed to initialize WalletConnect: ${this.getErrorMessage(error)}`, 'WALLETCONNECT_INIT_FAILED');
        }
    }
    async ensureWalletConnectConnected(provider) {
        var _a;
        const hasSession = Boolean(provider.session);
        const hasAccounts = Array.isArray(provider.accounts) && provider.accounts.length > 0;
        if (hasSession || hasAccounts) {
            return;
        }
        if (!provider.connect) {
            throw new AuthError('WalletConnect provider does not support connect()', 'WALLETCONNECT_CONNECT_UNSUPPORTED');
        }
        const chains = ((_a = this.config.walletConnectChains) === null || _a === void 0 ? void 0 : _a.length) ? this.config.walletConnectChains : [1];
        try {
            await provider.connect({ optionalChains: chains, chains });
        }
        catch (error) {
            throw new AuthError(`Failed to connect WalletConnect session: ${this.getErrorMessage(error)}`, 'WALLETCONNECT_CONNECT_FAILED');
        }
    }
    async signMessageWithWalletConnect(provider, message, walletAddress) {
        const messageHex = this.toHex(message);
        const attempts = [
            [messageHex, walletAddress],
            [walletAddress, messageHex],
            [message, walletAddress],
        ];
        for (let i = 0; i < attempts.length; i += 1) {
            try {
                return (await provider.request({
                    method: 'personal_sign',
                    params: attempts[i],
                }));
            }
            catch (error) {
                if (i === attempts.length - 1) {
                    throw new AuthError(`Failed to sign message with WalletConnect: ${this.getErrorMessage(error)}`, 'WALLETCONNECT_SIGN_FAILED');
                }
            }
        }
        throw new AuthError('Failed to sign message with WalletConnect', 'WALLETCONNECT_SIGN_FAILED');
    }
    toHex(value) {
        const bytes = new TextEncoder().encode(value);
        let hex = '0x';
        bytes.forEach((byte) => {
            hex += byte.toString(16).padStart(2, '0');
        });
        return hex;
    }
    getErrorMessage(error) {
        if (error instanceof Error && error.message) {
            return error.message;
        }
        if (typeof error === 'string' && error.trim()) {
            return error;
        }
        return 'Unknown error';
    }
    async fetchSiweNonce(walletAddress) {
        const nonceResponse = await fetch(`${this.config.baseUrl}${this.ENDPOINTS.SIWE_NONCE}?client_id=${this.config.clientId}&evm_wallet_address=${walletAddress}`, { method: 'GET' });
        if (!nonceResponse.ok) {
            throw new AuthError('Failed to get nonce', 'NONCE_FAILED', nonceResponse.status);
        }
        return (await nonceResponse.json());
    }
    async fetchSiweMessage(nonce) {
        const messageResponse = await fetch(`${this.config.baseUrl}${this.ENDPOINTS.SIWE_MESSAGE}?client_id=${this.config.clientId}&nonce=${nonce}`, { method: 'GET' });
        if (!messageResponse.ok) {
            throw new AuthError('Failed to get message', 'MESSAGE_FAILED', messageResponse.status);
        }
        return (await messageResponse.json());
    }
    async exchangeSiweToken(signature, walletAddress, chainId) {
        const tokenResponse = await fetch(`${this.config.baseUrl}${this.ENDPOINTS.TOKEN}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'siwe',
                client_id: this.config.clientId,
                signature,
                evm_wallet_address: walletAddress,
                chain_id: chainId,
                scope: this.config.scope,
            })
        });
        if (!tokenResponse.ok) {
            throw new AuthError('Failed to get tokens', 'TOKEN_FAILED', tokenResponse.status);
        }
        return await tokenResponse.json();
    }
}
